import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

/** Select value that switches the location field to typing a new one. */
const NEW_LOCATION = '__new__';

interface ProductionLine {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  spool_type?: 'small' | 'big';
  external_code?: string | null;
  location?: string | null;
  ip_address?: string | null;
}

interface User {
  id: number;
  name: string;
  email: string;
}

const ProductionLines: React.FC = () => {
  const { isAdmin, isTracker, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [lines, setLines] = useState<ProductionLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLine, setEditingLine] = useState<ProductionLine | null>(null);
  // Location is picked from the ones already given to lines; "+ Nowa lokalizacja" switches to typing one.
  const [typingLocation, setTypingLocation] = useState(false);
  const [formData, setFormData] = useState<{ name: string; description: string; is_active: boolean; spool_type: 'small' | 'big'; external_code: string; location: string; ip_address: string }>({ name: '', description: '', is_active: true, spool_type: 'small', external_code: '', location: '', ip_address: '' });
  const [showUserAssignment, setShowUserAssignment] = useState<number | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<number[]>([]);
  // IDs linii przypisanych do zalogowanego usera (do filtrowania widoku dla zwykłego usera)
  const [myLineIds, setMyLineIds] = useState<number[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  // Drag and drop of the rows (pointer events — mouse and touch alike).
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const orderBeforeDrag = useRef<ProductionLine[]>([]);
  const linesRef = useRef<ProductionLine[]>([]);
  linesRef.current = lines;

  // Zwykły użytkownik (nie admin, nie kierownik, nie pakowanie) widzi tylko przypisane linie.
  const isRegularUser = user?.role === 'user';

  useEffect(() => {
    fetchLines();
    if (isAdmin) {
      fetchUsers();
    }
    if (isRegularUser) {
      fetchMyLines();
    }
  }, [isAdmin, isRegularUser]);

  const fetchLines = async () => {
    setLoading(true);
    try {
      const response = await api.get('/production-lines');
      setLines(response.data);
    } catch (error) {
      console.error('Error fetching production lines:', error);
    } finally {
      setLoading(false);
    }
  };

  // The order set here is the order of the lines in every list in the app.
  const saveOrder = async (next: ProductionLine[], previous: ProductionLine[]) => {
    setSavingOrder(true);
    try {
      const response = await api.put('/production-lines/order', { ids: next.map((l) => l.id) });
      setLines(response.data);
    } catch (error: any) {
      setLines(previous);
      alert(t('common.error') + ': ' + (error.response?.data?.message || t('table.couldNotSaveOrder')));
    } finally {
      setSavingOrder(false);
    }
  };

  const startDrag = (e: React.PointerEvent<HTMLElement>, lineId: number) => {
    if (savingOrder || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    orderBeforeDrag.current = linesRef.current;
    setDraggingId(lineId);
  };

  // The dragged row takes the place of the row under the pointer.
  const dragOver = (e: React.PointerEvent<HTMLElement>) => {
    if (draggingId === null) return;
    const row = document.elementFromPoint(e.clientX, e.clientY)?.closest('tr[data-line-id]');
    const targetId = row ? Number(row.getAttribute('data-line-id')) : null;
    if (targetId === null || targetId === draggingId) return;
    const current = linesRef.current;
    const from = current.findIndex((l) => l.id === draggingId);
    const to = current.findIndex((l) => l.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    // The ref too: the pointer can be released before React renders this move.
    linesRef.current = next;
    setLines(next);
  };

  const endDrag = (cancelled: boolean) => {
    if (draggingId === null) return;
    setDraggingId(null);
    const previous = orderBeforeDrag.current;
    const next = linesRef.current;
    if (cancelled) {
      setLines(previous);
      return;
    }
    if (next.some((line, i) => line.id !== previous[i]?.id)) {
      saveOrder(next, previous);
    }
  };

  const fetchMyLines = async () => {
    try {
      const response = await api.get('/my-production-lines');
      setMyLineIds((response.data || []).map((l: ProductionLine) => l.id));
    } catch (error) {
      console.error('Error fetching assigned lines:', error);
      setMyLineIds([]);
    }
  };

  // Ustaw wybraną linię i przejdź do "Moja produkcja".
  // Linię przekazujemy w URL (?line=ID) — MyProduction reaguje na param za każdym razem,
  // niezależnie od tego, czy komponent się przemontował. Dodatkowo zapisujemy w localStorage.
  const goToLineInProduction = (lineId: number) => {
    if (user?.id) {
      localStorage.setItem(`selectedLineId_${user.id}`, String(lineId));
    } else {
      localStorage.setItem('selectedLineId', String(lineId));
    }
    navigate(`/?line=${lineId}`);
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAssignedUsers = async (lineId: number) => {
    try {
      const response = await api.get(`/production-lines/${lineId}/users`);
      setAssignedUsers(response.data.map((u: User) => u.id));
    } catch (error) {
      console.error('Error fetching assigned users:', error);
    }
  };

  const handleAssignUsers = async (lineId: number) => {
    try {
      await api.post(`/production-lines/${lineId}/assign-users`, {
        user_ids: assignedUsers,
      });
      alert(t('table.usersAssigned'));
      setShowUserAssignment(null);
      setAssignedUsers([]);
    } catch (error: any) {
      alert(t('common.error') + ': ' + (error.response?.data?.message || t('table.couldNotAssignUsers')));
    }
  };

  const handleOpenUserAssignment = (lineId: number) => {
    setShowUserAssignment(lineId);
    fetchAssignedUsers(lineId);
  };

  const handleOpenCreate = () => {
    setTypingLocation(false);
    setEditingLine(null);
    setFormData({ name: '', description: '', is_active: true, spool_type: 'small', external_code: '', location: '', ip_address: '' });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingLine) {
        await api.put(`/production-lines/${editingLine.id}`, formData);
      } else {
        await api.post('/production-lines', formData);
      }
      fetchLines();
      setShowForm(false);
      setEditingLine(null);
      setFormData({ name: '', description: '', is_active: true, spool_type: 'small', external_code: '', location: '', ip_address: '' });
      alert(editingLine ? t('table.lineUpdated') : t('table.lineCreated'));
    } catch (error: any) {
      console.error('Error saving production line:', error);
      alert(t('common.error') + ': ' + (error.response?.data?.message || t('table.couldNotSaveLine')));
    }
  };

  const handleEdit = (line: ProductionLine) => {
    setTypingLocation(false);
    setEditingLine(line);
    setFormData({ name: line.name, description: line.description || '', is_active: line.is_active, spool_type: line.spool_type === 'big' ? 'big' : 'small', external_code: line.external_code || '', location: line.location || '', ip_address: line.ip_address || '' });
    setShowForm(true);
  };

  const statusBadge = (active: boolean) => (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: active ? '#d1fae5' : '#fee2e2',
        color: active ? '#065f46' : '#991b1b',
      }}
    >
      {active ? t('table.active') : t('table.inactive')}
    </span>
  );

  // Zwykły user z przypisaniami widzi tylko swoje linie; reszta (admin, kierownik,
  // user bez przypisań) jak dotychczas — wszystkie.
  const userHasAssignments = isRegularUser && myLineIds.length > 0;
  const displayedLines = userHasAssignments
    ? lines.filter((l) => myLineIds.includes(l.id))
    : lines;
  // Kolumna akcji: admin (pełne akcje), zwykły user z przypisaniami lub tracker — dla tych
  // dwóch ostatnich zostaje sam „Podgląd", bo to ich jedyne wejście w linię.
  const showActionsColumn = isAdmin || userHasAssignments || isTracker;
  const knownLocations = Array.from(
    new Set(lines.map((l) => l.location?.trim()).filter((l): l is string => !!l)),
  ).sort((a, b) => a.localeCompare(b, 'pl'));
  const columnCount = 5 + (showActionsColumn ? 1 : 0) + (isAdmin ? 1 : 0);

  return (
    <div>
      <div className="page-header" style={{ padding: '8px 0', marginBottom: 20 }}>
        <h1
          style={{
            color: '#2d3748',
            background: 'none',
            WebkitBackgroundClip: 'initial',
            WebkitTextFillColor: '#2d3748',
            backgroundClip: 'initial',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ WebkitTextFillColor: 'initial' }}>🏭</span>
          {t('nav.productionLines')}
        </h1>
        {isAdmin && (
          <button
            onClick={handleOpenCreate}
            className="btn"
            style={{ backgroundColor: '#667eea', color: '#ffffff', fontWeight: 600 }}
          >
            + {t('table.addProductionLine')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                {isAdmin && (
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', width: 90 }}>
                    {t('table.order')}
                  </th>
                )}
                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('table.name')}
                </th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('table.description')}
                </th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('table.location')}
                </th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('table.spoolType')}
                </th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('table.status')}
                </th>
                {showActionsColumn && (
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('table.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {displayedLines.length === 0 && (
                <tr>
                  <td
                    colSpan={columnCount}
                    style={{
                      padding: '48px 20px',
                      textAlign: 'center',
                      color: '#718096',
                      fontStyle: 'italic',
                    }}
                  >
                    {t('table.noData')}
                  </td>
                </tr>
              )}
              {displayedLines.map((line) => (
                <tr
                  key={line.id}
                  data-line-id={line.id}
                  style={{
                    borderBottom: '1px solid #e1e8ed',
                    backgroundColor: draggingId === line.id ? '#eef2ff' : line.is_active ? 'white' : '#f8f9fa',
                    boxShadow: draggingId === line.id ? 'inset 4px 0 0 #667eea' : 'none',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {isAdmin && (
                    <td
                      onPointerDown={(e) => startDrag(e, line.id)}
                      onPointerMove={dragOver}
                      onPointerUp={() => endDrag(false)}
                      onPointerCancel={() => endDrag(true)}
                      title={t('table.dragToReorder')}
                      aria-label={t('table.dragToReorder')}
                      style={{
                        padding: '14px 12px', textAlign: 'center', fontSize: 22, lineHeight: 1,
                        color: draggingId === line.id ? '#667eea' : '#94a3b8',
                        cursor: savingOrder ? 'wait' : draggingId !== null ? 'grabbing' : 'grab',
                        touchAction: 'none', userSelect: 'none',
                      }}
                    >
                      ⠿
                    </td>
                  )}
                  <td style={{ padding: '14px 12px', fontWeight: 500, color: '#2d3748' }}>{line.name}</td>
                  <td style={{ padding: '14px 12px', color: '#4a5568' }}>{line.description || '-'}</td>
                  <td style={{ padding: '14px 12px', color: '#2d3748', fontWeight: 500 }}>{line.location || '-'}</td>
                  <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: line.spool_type === 'big' ? '#e0e7ff' : '#f1f5f9',
                        color: line.spool_type === 'big' ? '#3730a3' : '#475569',
                      }}
                    >
                      {line.spool_type === 'big' ? t('table.spoolTypeBig') : t('table.spoolTypeSmall')}
                    </span>
                  </td>
                  <td style={{ padding: '14px 12px', textAlign: 'center' }}>{statusBadge(line.is_active)}</td>
                  {showActionsColumn && (
                    <td style={{ padding: '14px 12px' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => navigate(`/admin/production-lines/${line.id}/view`)}
                          className="btn"
                          style={{ padding: '6px 12px', fontSize: 13, backgroundColor: '#10b981', color: 'white' }}
                        >
                          {t('table.view')}
                        </button>
                        {/* Tracker nie produkuje — „Przejdź do produkcji" prowadziłoby go
                            do widoku, w którym i tak nic nie może zrobić. */}
                        {!isTracker && (
                          <button
                            onClick={() => goToLineInProduction(line.id)}
                            className="btn"
                            style={{ padding: '6px 12px', fontSize: 13, backgroundColor: '#667eea', color: 'white' }}
                          >
                            {t('table.goToProduction')}
                          </button>
                        )}
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handleOpenUserAssignment(line.id)}
                              className="btn"
                              style={{ padding: '6px 12px', fontSize: 13, backgroundColor: '#667eea', color: 'white' }}
                            >
                              {t('table.assignUsers')}
                            </button>
                            <button
                              onClick={() => handleEdit(line)}
                              className="btn"
                              style={{ padding: '6px 12px', fontSize: 13, backgroundColor: 'white', color: '#667eea', border: '2px solid #667eea' }}
                            >
                              {t('common.edit')}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && isAdmin && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ marginTop: 0, color: '#667eea' }}>
              {editingLine ? t('table.editProductionLine') : t('table.addProductionLine')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
                  {t('table.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input"
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
                  {t('table.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="input"
                  style={{ resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
                  {t('table.location')}
                </label>
                {knownLocations.length > 0 && !typingLocation ? (
                  <select
                    value={formData.location}
                    onChange={(e) => {
                      if (e.target.value === NEW_LOCATION) {
                        setTypingLocation(true);
                        setFormData({ ...formData, location: '' });
                      } else {
                        setFormData({ ...formData, location: e.target.value });
                      }
                    }}
                    className="input"
                  >
                    <option value="">{t('table.locationNone')}</option>
                    <option value={NEW_LOCATION}>{t('table.locationNew')}</option>
                    {knownLocations.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      autoFocus={typingLocation}
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      maxLength={100}
                      className="input"
                      placeholder={t('table.locationPlaceholder')}
                    />
                    {knownLocations.length > 0 && (
                      <button
                        type="button"
                        className="btn"
                        style={{ whiteSpace: 'nowrap', backgroundColor: 'white', color: '#667eea', border: '2px solid #667eea' }}
                        onClick={() => {
                          setTypingLocation(false);
                          setFormData({ ...formData, location: knownLocations.includes(formData.location.trim()) ? formData.location.trim() : '' });
                        }}
                      >
                        {t('table.locationPick')}
                      </button>
                    )}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{t('table.locationHint')}</div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
                  {t('table.externalCode')}
                </label>
                <input
                  type="text"
                  value={formData.external_code}
                  onChange={(e) => setFormData({ ...formData, external_code: e.target.value })}
                  className="input"
                  placeholder="Pecice_L1"
                />
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{t('table.externalCodeHint')}</div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
                  {t('table.ipAddress')}
                </label>
                <input
                  type="text"
                  value={formData.ip_address}
                  onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                  className="input"
                  placeholder="10.0.65.31:4840"
                  maxLength={255}
                />
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{t('table.ipAddressHint')}</div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
                  {t('table.spoolType')}
                </label>
                <select
                  value={formData.spool_type}
                  onChange={(e) => setFormData({ ...formData, spool_type: e.target.value === 'big' ? 'big' : 'small' })}
                  className="input"
                >
                  <option value="small">{t('table.spoolTypeSmall')}</option>
                  <option value="big">{t('table.spoolTypeBig')}</option>
                </select>
                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#718096' }}>
                  {formData.spool_type === 'big' ? t('table.spoolTypeBigHint') : t('table.spoolTypeSmallHint')}
                </p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span style={{ color: '#4a5568' }}>{t('table.active')}</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingLine(null);
                  }}
                  className="btn"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{ backgroundColor: '#667eea', color: 'white' }}
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showUserAssignment && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ marginTop: 0, color: '#667eea' }}>
              {t('table.assignUsersToLine', { lineName: lines.find((l) => l.id === showUserAssignment)?.name || '' })}
            </h2>
            <div style={{ marginTop: 10, overflowY: 'auto', flex: 1 }}>
              {allUsers.map((user) => (
                <label
                  key={user.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 12px',
                    marginBottom: 6,
                    borderRadius: 8,
                    border: '1px solid #e1e8ed',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                >
                  <input
                    type="checkbox"
                    checked={assignedUsers.includes(user.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setAssignedUsers([...assignedUsers, user.id]);
                      } else {
                        setAssignedUsers(assignedUsers.filter((id) => id !== user.id));
                      }
                    }}
                    style={{ marginRight: 12, width: 18, height: 18 }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, color: '#2d3748' }}>{user.name}</div>
                    <div style={{ fontSize: 12, color: '#718096' }}>{user.email}</div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowUserAssignment(null);
                  setAssignedUsers([]);
                }}
                className="btn"
                style={{ backgroundColor: '#6c757d', color: 'white' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleAssignUsers(showUserAssignment)}
                className="btn"
                style={{ backgroundColor: '#10b981', color: 'white' }}
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionLines;
